// External packages
import * as React from 'react';
import { z } from 'zod';

// Components
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Form, InputField } from 'components/Form';
import { Icon } from 'components/Icons';
import * as Modal from 'components/Modal';

// Hooks
import {
  useCreateApiKey,
  useGetApiKeys,
  useRevokeApiKey,
  type CreatedApiKey,
} from 'api/apiKey';
import { useToast } from 'hooks/useToast';

const createApiKeySchema = z.object({
  name: z.string().optional(),
});

/**
 * API key management (docs/MULTI_TENANCY.md, Phase 2).
 *
 * Keys authenticate mobile apps against the v2 API and belong to the
 * logged-in user's organization. The full key is displayed exactly once,
 * right after creation — only a hash is stored server-side.
 */
export default function ApiKeysPage() {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [createdKey, setCreatedKey] = React.useState<CreatedApiKey | null>(null);

  const toast = useToast();
  const { data: apiKeys, isLoading } = useGetApiKeys();
  const { mutate: createApiKey, isPending: isCreating } = useCreateApiKey();
  const { mutate: revokeApiKey } = useRevokeApiKey();

  const handleCreate = (data: z.infer<typeof createApiKeySchema>) => {
    createApiKey(
      { name: data.name || undefined },
      {
        onSuccess: (key) => {
          setModalOpen(false);
          setCreatedKey(key);
        },
      }
    );
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success('Ključ je kopiran u međuspremnik!');
    } catch {
      toast.error('Kopiranje nije uspjelo — kopirajte ključ ručno.');
    }
  };

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString('hr-HR') : '—';

  return (
    <Card className="flex flex-col h-full min-h-[calc(100vh-9rem)] md:min-h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between gap-x-8 gap-y-4">
        <h1 className="text-2xl text-gray-600">API ključevi</h1>
        <Modal.TriggerRoot isOpen={modalOpen} onOpenChange={setModalOpen}>
          <Button icon={<Icon name="plus" />}>Dodaj ključ</Button>
          <Modal.Overlay isDismissable>
            <Modal.Root className="max-w-lg">
              <Modal.Dialog>
                <Modal.Close />
                <h3 className="mb-8 text-2xl text-gray-600">Novi API ključ</h3>
                <Form schema={createApiKeySchema} onSubmit={handleCreate}>
                  <InputField
                    name="name"
                    inputProps={{ colorScheme: 'white' }}
                    label="Naziv (npr. iOS produkcija)"
                    className="flex-1"
                  />
                  <p className="mb-4 text-xs text-gray-500">
                    Ključ služi mobilnim aplikacijama za pristup podacima vaše
                    organizacije. Prikazuje se samo jednom nakon kreiranja.
                  </p>
                  <Button
                    type="submit"
                    className="w-full mt-8"
                    size="lg"
                    isPending={isCreating}
                  >
                    Stvori ključ
                  </Button>
                </Form>
              </Modal.Dialog>
            </Modal.Root>
          </Modal.Overlay>
        </Modal.TriggerRoot>
      </div>

      <hr className="my-6 h-px text-gray-200" />

      {/* One-time display of a freshly created key */}
      {createdKey && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="mb-2 font-medium text-amber-800">
            Spremite ovaj ključ — više se neće prikazati!
          </p>
          <div className="flex items-center gap-3">
            <code className="flex-1 overflow-x-auto rounded bg-white px-3 py-2 text-sm text-gray-800">
              {createdKey.key}
            </code>
            <Button size="sm" onPress={() => copyKey(createdKey.key)}>
              Kopiraj
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="secondary"
              onPress={() => setCreatedKey(null)}
            >
              Zatvori
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Icon name="loader" className="size-10 animate-spin text-blue-500" />
        </div>
      ) : apiKeys.length === 0 ? (
        <p className="text-gray-500">
          Nema API ključeva. Kreirajte prvi ključ za mobilnu aplikaciju.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="py-3 pr-4 font-medium">Naziv</th>
                <th className="py-3 pr-4 font-medium">Ključ</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium">Stvoren</th>
                <th className="py-3 pr-4 font-medium">Zadnje korišten</th>
                <th className="py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((apiKey) => (
                <tr key={apiKey.id} className="border-b border-gray-100">
                  <td className="py-3 pr-4 text-gray-700">
                    {apiKey.name || '—'}
                  </td>
                  <td className="py-3 pr-4">
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs">
                      {apiKey.keyPrefix}…
                    </code>
                  </td>
                  <td className="py-3 pr-4">
                    {apiKey.active ? (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs text-green-700">
                        Aktivan
                      </span>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">
                        Opozvan
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-gray-500">
                    {formatDate(apiKey.createdAt)}
                  </td>
                  <td className="py-3 pr-4 text-gray-500">
                    {formatDate(apiKey.lastUsedAt)}
                  </td>
                  <td className="py-3 text-right">
                    {apiKey.active && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="tertiary"
                        icon={<Icon name="trash" />}
                        onPress={() => {
                          if (
                            window.confirm(
                              'Opozvati ovaj ključ? Aplikacije koje ga koriste izgubit će pristup.'
                            )
                          ) {
                            revokeApiKey({ id: apiKey.id });
                          }
                        }}
                      >
                        Opozovi
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
